import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Download,
  ImageUp,
  Loader2,
  RefreshCw,
  Save,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import type { CarouselSlide, DraftDTO } from "@app/shared";
import { api, carouselSlideUrl, uploadCarouselImage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../components/Toast";

function msg(e: unknown): string {
  return e instanceof Error ? e.message : "Terjadi kesalahan";
}

/**
 * Carousel detail (`/carousel/:id`): previews every rendered slide and lets the
 * operator edit each slide's copy, swap its stock photo, or upload a custom image
 * (e.g. a diagram). Edits re-render that single slide in the background; the page
 * polls the draft until all slides have an image.
 */
export function CarouselDetail() {
  const { id = "" } = useParams();

  const draftQuery = useQuery({
    queryKey: ["draft", id],
    queryFn: ({ signal }) => api.draft(id, signal),
    refetchInterval: (query) => {
      const d = query.state.data as DraftDTO | undefined;
      const slides = d?.slides ?? [];
      const pending = slides.length === 0 || slides.some((s) => !s.imageFile);
      return pending ? 4000 : false;
    },
  });
  const draft = draftQuery.data;
  const slides = draft?.slides ?? [];

  return (
    <div className="space-y-5">
      <Link
        to="/carousel"
        className="flex w-fit items-center gap-1.5 text-sm text-slate-500 hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" />
        Semua carousel
      </Link>

      {draftQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : !draft ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-slate-500">
            Carousel tidak ditemukan.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold tracking-tight text-navy">
                {draft.headline ?? "Carousel"}
              </h1>
              <p className="text-sm text-slate-500">
                {slides.length} slide · status {draft.status}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {slides.map((s, i) => (
              <SlideEditor
                key={i}
                draftId={id}
                index={i}
                slide={s}
                total={slides.length}
                version={draft.updatedAt}
              />
            ))}
          </div>

          {draft.caption ? (
            <Card>
              <CardContent className="space-y-2 py-4">
                <p className="text-sm font-semibold text-navy">Caption</p>
                <p className="whitespace-pre-wrap text-sm text-slate-700">
                  {draft.caption}
                </p>
                {draft.hashtags.length > 0 ? (
                  <p className="text-sm text-teal">
                    {draft.hashtags
                      .map((h) => (h.startsWith("#") ? h : `#${h}`))
                      .join(" ")}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

/** One slide: rendered preview + copy editor + image swap/upload. */
function SlideEditor({
  draftId,
  index,
  slide,
  total,
  version,
}: {
  draftId: string;
  index: number;
  slide: CarouselSlide;
  total: number;
  version: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [heading, setHeading] = useState(slide.heading ?? "");
  const [subheading, setSubheading] = useState(slide.subheading ?? "");
  const [body, setBody] = useState(slide.body ?? "");
  const [bullets, setBullets] = useState((slide.bullets ?? []).join("\n"));
  const [citation, setCitation] = useState(slide.citation ?? "");
  const [contact, setContact] = useState(slide.contact ?? "");

  // Re-render is async in the worker; refetch the draft a few times as it lands.
  const refresh = () =>
    [1500, 4000, 8000, 14000].forEach((ms) =>
      setTimeout(
        () => void qc.invalidateQueries({ queryKey: ["draft", draftId] }),
        ms,
      ),
    );

  const save = useMutation({
    mutationFn: () =>
      api.updateCarouselSlide(draftId, index, {
        heading: heading.trim(),
        subheading:
          slide.role === "cover" ? subheading.trim() || null : undefined,
        body:
          slide.role === "content" || slide.role === "cta"
            ? body.trim() || null
            : undefined,
        bullets:
          slide.role === "content"
            ? bullets
                .split("\n")
                .map((b) => b.trim())
                .filter(Boolean)
            : undefined,
        citation:
          slide.role === "content" ? citation.trim() || null : undefined,
        contact: slide.role === "cta" ? contact.trim() || null : undefined,
      }),
    onSuccess: () => {
      toast.success("Slide diperbarui — merender ulang…");
      refresh();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const swap = useMutation({
    mutationFn: () => api.swapCarouselImage(draftId, index, {}),
    onSuccess: () => {
      toast.success("Mengganti foto…");
      refresh();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadCarouselImage(draftId, index, file),
    onSuccess: () => {
      toast.success("Gambar diunggah — merender ulang…");
      refresh();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const roleLabel =
    slide.role === "cover"
      ? "Cover"
      : slide.role === "cta"
        ? "CTA / Penutup"
        : `Slide ${index}`;
  const busy = save.isPending || swap.isPending || upload.isPending;

  return (
    <Card>
      <CardContent className="grid grid-cols-1 gap-5 py-5 sm:grid-cols-[300px_1fr]">
        <div className="space-y-2">
          <div className="relative aspect-[4/5] overflow-hidden rounded-md border border-slate-200 bg-slate-100">
            {slide.imageFile ? (
              <img
                src={carouselSlideUrl(draftId, index, version)}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-slate-400">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                merender…
              </div>
            )}
            <span className="absolute left-2 top-2 rounded bg-navy/85 px-2 py-0.5 text-[11px] font-semibold text-white">
              {index + 1}/{total} · {roleLabel}
            </span>
          </div>
          <div className="flex gap-2">
            {slide.role !== "cta" ? (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => swap.mutate()}
                  disabled={busy}
                >
                  {swap.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Ganti foto
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload.mutate(f);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                >
                  {upload.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImageUp className="h-4 w-4" />
                  )}
                  Upload
                </Button>
              </>
            ) : null}
            {slide.imageFile ? (
              <a
                href={carouselSlideUrl(draftId, index, version)}
                download={`slide-${index + 1}.png`}
                className="flex items-center justify-center rounded-md border border-slate-200 px-3 text-slate-500 hover:bg-slate-50 hover:text-navy"
                title="Unduh slide"
              >
                <Download className="h-4 w-4" />
              </a>
            ) : null}
          </div>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Judul</Label>
            <Input value={heading} onChange={(e) => setHeading(e.target.value)} />
          </div>
          {slide.role === "cover" ? (
            <div className="space-y-1.5">
              <Label>Subjudul</Label>
              <Input
                value={subheading}
                onChange={(e) => setSubheading(e.target.value)}
              />
            </div>
          ) : null}
          {slide.role === "content" || slide.role === "cta" ? (
            <div className="space-y-1.5">
              <Label>Isi</Label>
              <Textarea
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
          ) : null}
          {slide.role === "content" ? (
            <div className="space-y-1.5">
              <Label>Poin (satu per baris)</Label>
              <Textarea
                rows={3}
                value={bullets}
                onChange={(e) => setBullets(e.target.value)}
              />
            </div>
          ) : null}
          {slide.role === "content" ? (
            <div className="space-y-1.5">
              <Label>Sumber (opsional)</Label>
              <Input
                value={citation}
                placeholder="Sumber: American Heart Association"
                onChange={(e) => setCitation(e.target.value)}
              />
            </div>
          ) : null}
          {slide.role === "cta" ? (
            <div className="space-y-1.5">
              <Label>Kontak</Label>
              <Input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />
            </div>
          ) : null}
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={busy || heading.trim().length === 0}
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Simpan & render
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
